import { graphql } from 'react-relay';
import { useCallback, useState, useEffect } from 'react';
import type { ApolloError } from '@confluence/apollo-relay-interop/entry-points/apolloTypes.ts';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { useWriteFragmentInterop } from '@confluence/apollo-relay-interop/entry-points/useWriteFragmentInterop.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import type { useHomeWidgetStateUserPreferencesFragment$data } from './__generated__/useHomeWidgetStateUserPreferencesFragment.graphql.ts';
import useHomeWidgetStateUserPreferencesFragment_artifact from './__generated__/useHomeWidgetStateUserPreferencesFragment.graphql.ts';
import type {
	useHomeWidgetStateQuery$data,
	useHomeWidgetStateQuery$variables,
	HomeWidgetState,
} from './__generated__/useHomeWidgetStateQuery.graphql.ts';

const useHomeWidgetStateQuery = graphql`
	query useHomeWidgetStateQuery {
		userPreferences {
			homeWidgets {
				id
				state
				__typename
			}
			__typename
		}
	}
`;

const useHomeWidgetStateHomeWidgetsMutation = graphql`
	mutation useHomeWidgetStateHomeWidgetsMutation($id: ID!, $state: HomeWidgetState!) {
		updateUserPreferences(userPreferences: { homeWidget: { id: $id, state: $state } }) {
			homeWidgets {
				id
				state
			}
		}
	}
`;

export const useHomeWidgetState = (
	id: string,
	defaultState?: HomeWidgetState,
	initialStateOverride?: HomeWidgetState,
): {
	widgetState: HomeWidgetState;
	setWidgetState: (newState: HomeWidgetState) => void;
	loading: boolean;
	error: ApolloError | undefined;
} => {
	const { isLicensed } = useSessionData();
	const {
		data,
		loading,
		error: queryError,
	} = useQueryInterop<useHomeWidgetStateQuery$data, useHomeWidgetStateQuery$variables>(
		useHomeWidgetStateQuery,
		{
			errorPolicy: 'all',
			// Use cache-first for both licensed and unlicensed users to allow cache reads
			fetchPolicy: 'cache-first',
		},
	);
	const [updateWidgetState, { error: mutationError }] = useMutationInterop(
		useHomeWidgetStateHomeWidgetsMutation,
	);
	const [stateOverride, setStateOverride] = useState(initialStateOverride);
	useEffect(() => {
		setStateOverride(initialStateOverride);
	}, [initialStateOverride]);
	// Use the widget state override, falling back to saved prefrence, falling back to defaultState, falling back to collapsed
	const widgetState =
		stateOverride ||
		data?.userPreferences?.homeWidgets.find((widget) => widget.id === id)?.state ||
		defaultState ||
		'COLLAPSED';
	const writeFragmentInterop = useWriteFragmentInterop<
		Omit<useHomeWidgetStateUserPreferencesFragment$data, ' $fragmentType'>
	>({
		fragmentArtifact: useHomeWidgetStateUserPreferencesFragment_artifact,
		fragmentSpec: graphql`
			fragment useHomeWidgetStateUserPreferencesFragment on UserPreferences {
				homeWidgets {
					id
					state
					__typename
				}
				__typename
			}
		`,
	});
	const setWidgetState = useCallback(
		(newState: HomeWidgetState): void => {
			if (isLicensed) {
				void updateWidgetState({
					variables: {
						id,
						state: newState,
					},
				});
			}
			const homeWidgets = (data?.userPreferences?.homeWidgets || []).filter(
				(widget) => widget.id !== id,
			);
			homeWidgets.push({
				id,
				state: newState,
				__typename: 'HomeWidget',
			});
			/**
			 * This write into cache serves two purposes:
			 *   1. To initially populate the cache in the case where
			 *      a preference hasn't yet been saved or the user is unlicensed.
			 *   2. To optimistically update the cache, since we're doing #1 anyway
			 */

			writeFragmentInterop({
				fragmentId: '$ROOT_QUERY.userPreferences',
				data: {
					homeWidgets,
					__typename: 'UserPreferences',
				},
			});

			// After the user explicitly changes state, clear the state override
			setStateOverride(undefined);
		},
		[id, data, isLicensed, updateWidgetState, writeFragmentInterop],
	);

	const error = mutationError || queryError;

	return { widgetState, setWidgetState, loading, error };
};
